import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Company from 'src/app/model/company/company';
import { ExplicitlyCompletableTask } from 'src/app/model/task-list/explicitly-completable-task';
import { LoadTask } from 'src/app/model/task-list/load-task';
import { SubProjectTask } from 'src/app/model/task-list/subproject-task';
import { Task, TASK_TYPES } from 'src/app/model/task-list/task';
import { TimeBudgettedTask } from 'src/app/model/task-list/time-budgetted-task';
import OfflineTaskStorageHelper from 'src/app/pages/projects/placer/shared-helper/offline-task-storage-helper';
import { FormatMoment } from 'src/app/pipes/format-moment';
import { OfflineTaskChangesObject } from 'src/app/services/placer-offline-changes/task/offline-storage/offline-task-changes-object';

export default class PlacerPlanningViewGeneralTaskHelper {
  private constructor() {}

  public static preprocessTimeBudgettedTask(task: TimeBudgettedTask): void {
    this.calculateCompletableTaskExecuted(task);
    task.timeBudgetDisplay =
      task.minuteBudget == null ? null : task.minuteBudget.toString();
  }

  public static calculateCompletableTaskExecuted(
    task: ExplicitlyCompletableTask
  ): void {
    task.executedClass =
      task.complete == null
        ? 'pending'
        : task.complete
        ? 'executed'
        : 'not-executed';
    if (task.executedClass === 'pending') {
      task.executedCircle = 'circle-pending';
      task.executedIconClass = this.getRegularExecutedIconClass(task);
    } else if (task.executedClass === 'executed') {
      task.executedCircle = 'circle-check';
      task.executedTimeString = GeneralHelper.getMinutesInHourAndMinutes(
        GeneralHelper.getTotalExecutionMinutes(task.registeredActions)
      );
      task.executedIconClass = 'uitgevoerd';
    } else {
      task.executedCircle = 'circle-cross';
      task.executedIconClass = this.getRegularExecutedIconClass(task);
    }
  }

  private static getRegularExecutedIconClass(
    task: ExplicitlyCompletableTask
  ): string {
    switch (task.type) {
      case TASK_TYPES.OTHER_TASK:
        return 'taak';
      case TASK_TYPES.LOAD_TASK:
        return 'load-task';
      default:
        throw new Error(`task type not recognized: ${task.type}`);
    }
  }

  public static calculateCompaniesDisplay(
    task: SubProjectTask | LoadTask
  ): void {
    if (task.subProject != null && task.subProject.project != null) {
      const involvedCompanies: Company[] = ProjectHelper.getDtoInvolvedCompanies(
        task.subProject.project
      );
      task.companiesDisplay = StringHelper.join(
        involvedCompanies.map(c => c.regularName),
        ', '
      );
    }
  }

  public static fillOfflineTaskInfo(
    task: ExplicitlyCompletableTask,
    taskIdToChanges: Map<number, OfflineTaskChangesObject>
  ): void {
    const changes = taskIdToChanges.get(task.id);
    if (changes != null) {
      task.complete =
        changes.complete != null ? changes.complete : task.complete;
      OfflineTaskStorageHelper.processOfflineRegisteredActions(task, changes);
    }
  }

  public static sortCompletedTasks(tasks: Task[]): void {
    if (CollectionHelper.isPopulated(tasks)) {
      tasks.sort(this.compareTasks);
    }
  }

  private static compareTasks(t1: Task, t2: Task): number {
    const t1Ordinal = PlacerPlanningViewGeneralTaskHelper.getExecutedClassOrdinal(
      t1
    );
    const t2Ordinal = PlacerPlanningViewGeneralTaskHelper.getExecutedClassOrdinal(
      t2
    );

    const nullCompare = GeneralHelper.compareNull(t1Ordinal, t2Ordinal);

    if (nullCompare != 0) {
      return nullCompare;
    }

    if (t1Ordinal != t2Ordinal) {
      return t1Ordinal - t2Ordinal;
    }

    return t1.sequenceNumber - t2.sequenceNumber;
  }

  private static getExecutedClassOrdinal(t: Task): number | null {
    switch (t.type) {
      case TASK_TYPES.SUB_PROJECT_TASK:
        return this.classToOrdinal((<SubProjectTask>t).executedClass);
      case TASK_TYPES.OTHER_TASK:
      case TASK_TYPES.LOAD_TASK:
        return this.classToOrdinal(
          (<ExplicitlyCompletableTask>t).executedClass
        );
      default:
        return null;
    }
  }

  private static classToOrdinal(c: string): number | null {
    switch (c) {
      case 'pending':
        return 0;
      case 'executed':
      case 'not-executed':
        return 1;
      default:
        return null;
    }
  }
}

export interface PlacerPlanningViewContext {
  formatMoment: FormatMoment;
}
